import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { api } from './api';

const AuthContext = createContext(null);

// ── LocalStorage keys synchronisés avec la DB ──────────────────────────────
const readFromLS = () => {
  try {
    const nc  = localStorage.getItem('ds_nav_config');
    const nmc = localStorage.getItem('ds_nav_mobile_count');
    const pso = localStorage.getItem('profile_section_order');
    const psh = localStorage.getItem('profile_section_hidden');
    return {
      ...(nc  ? { nav_config:             JSON.parse(nc)          } : {}),
      ...(nmc ? { nav_mobile_count:        parseInt(nmc, 10)       } : {}),
      ...(pso ? { profile_section_order:   JSON.parse(pso)         } : {}),
      ...(psh ? { profile_section_hidden:  JSON.parse(psh)         } : {}),
    };
  } catch { return {}; }
};

const writeToLS = (s) => {
  try {
    if (s.nav_config             !== undefined) localStorage.setItem('ds_nav_config',            JSON.stringify(s.nav_config));
    if (s.nav_mobile_count       !== undefined) localStorage.setItem('ds_nav_mobile_count',      String(s.nav_mobile_count));
    if (s.profile_section_order  !== undefined) localStorage.setItem('profile_section_order',    JSON.stringify(s.profile_section_order));
    if (s.profile_section_hidden !== undefined) localStorage.setItem('profile_section_hidden',   JSON.stringify(s.profile_section_hidden));
  } catch {}
};

// ── Provider ─────────────────────────────────────────────────────────────────
export function AuthProvider({ children }) {
  const [user, setUser] = useState(undefined);  // undefined=loading, null=unauth
  const [previewRole, setPreviewRole] = useState(null);
  // Initialiser depuis localStorage pour un rendu immédiat sans flicker
  const [settings, setSettings] = useState(() => readFromLS());
  const userRef = useRef(null);

  // Auth check
  useEffect(() => {
    if (window.location.hash?.includes('session_id=')) {
      setUser(undefined);
      return;
    }
    api.get('/auth/me')
      .then(u => setUser(u))
      .catch(() => setUser(null));
  }, []);

  // Sync settings from DB when user logs in (override localStorage)
  useEffect(() => {
    userRef.current = user;
    if (!user) return;
    api.get('/profile/settings')
      .then(dbSettings => {
        if (dbSettings && Object.keys(dbSettings).length > 0) {
          setSettings(prev => {
            const merged = { ...prev, ...dbSettings };
            writeToLS(merged);
            return merged;
          });
        }
      })
      .catch(() => {/* silently use localStorage */});
  }, [user?.user_id]);  // eslint-disable-line react-hooks/exhaustive-deps

  // Enregistrer un patch → localStorage (immédiat) + DB (async)
  const updateSettings = useCallback(async (patch) => {
    setSettings(prev => {
      const next = { ...prev, ...patch };
      writeToLS(next);
      return next;
    });
    if (userRef.current) {
      try { await api.post('/profile/settings', patch); }
      catch { /* silently ignore, localStorage is up to date */ }
    }
  }, []);

  const logout = async () => {
    await api.post('/auth/logout', {});
    setUser(null);
    setPreviewRole(null);
  };

  const effectiveRole = user?.role === 'admin' && previewRole ? previewRole : user?.role;

  return (
    <AuthContext.Provider value={{
      user, setUser, logout,
      previewRole, setPreviewRole, effectiveRole,
      settings, updateSettings,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
